import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getSessionId } from "@/lib/session";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Static routes that map 1:1 to backend ROUTE_OG_DATA keys.
const STATIC_ROUTES = {
  "/":              { key: "home",         label: "PSHL8 — Pro Skate Hockey League" },
  "/about":         { key: "about",        label: "About PSHL8" },
  "/teams":         { key: "teams",        label: "PSHL8 — The Franchise Eight" },
  "/schedule":      { key: "schedule",     label: "PSHL8 — Season Schedule" },
  "/standings":     { key: "standings",    label: "PSHL8 — League Standings" },
  "/players":       { key: "players",      label: "PSHL8 — Players & Draft" },
  "/news":          { key: "news",         label: "PSHL8 — News from the Rink" },
  "/sponsorships":  { key: "sponsorships", label: "Sponsor PSHL — Reach 1.4 Billion" },
  "/partnerships":  { key: "partnerships", label: "Partner With PSHL8" },
  "/academies":     { key: "academies",    label: "PSHL8 Academies" },
  "/media":         { key: "media",        label: "PSHL8 Media Centre" },
  "/contact":       { key: "contact",      label: "Contact PSHL8" },
};

function resolveShareTarget(pathname) {
  // /teams/:slug → /api/share/team/<slug>
  const teamMatch = pathname.match(/^\/teams\/([a-z0-9-]+)$/i);
  if (teamMatch) {
    return {
      kind: "team",
      targetId: teamMatch[1],
      shareUrl: `${BACKEND_URL}/api/share/team/${teamMatch[1]}`,
      label: "PSHL8 Franchise",
    };
  }
  // /news/:id → /api/share/news/<id>
  const newsMatch = pathname.match(/^\/news\/([a-z0-9-_]+)$/i);
  if (newsMatch) {
    return {
      kind: "news",
      targetId: newsMatch[1],
      shareUrl: `${BACKEND_URL}/api/share/news/${newsMatch[1]}`,
      label: "PSHL8 News",
    };
  }
  // Static routes
  const r = STATIC_ROUTES[pathname];
  if (r) {
    return {
      kind: "page-share",
      targetId: r.key,
      shareUrl: `${BACKEND_URL}/api/share/page/${r.key}`,
      label: r.label,
    };
  }
  return null;
}

export default function ShareThisPage() {
  const { pathname } = useLocation();
  const [copied, setCopied] = useState(false);

  // Hide on admin routes
  const target = useMemo(() => {
    if (pathname.startsWith("/admin")) return null;
    return resolveShareTarget(pathname);
  }, [pathname]);

  if (!target) return null;

  const logShare = () => {
    api.post("/shares", {
      kind: target.kind,
      target_id: target.targetId,
      page: pathname,
      session_id: getSessionId(),
    }).catch(() => { /* fire-and-forget */ });
  };

  const onShare = async () => {
    logShare();
    const shareData = {
      title: target.label,
      text: `${target.label} — Power · Speed · Hustle · Legacy`,
      url: target.shareUrl,
    };
    // Native share sheet (mobile + modern desktop)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // User dismissed the share sheet OR Web Share API blocked — fall through
        if (process.env.NODE_ENV !== "production") console.warn("[ShareThisPage] native share fallback", err);
      }
    }
    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(target.shareUrl);
      setCopied(true);
      toast.success("Share link copied", {
        description: target.shareUrl.replace(BACKEND_URL, ""),
      });
      setTimeout(() => setCopied(false), 2400);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[ShareThisPage] clipboard write failed", err);
      toast.error("Couldn't copy link — long-press to copy manually");
    }
  };

  return (
    <motion.button
      type="button"
      onClick={onShare}
      data-testid="share-this-page-btn"
      aria-label="Share this page"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.35, type: "spring", stiffness: 220, damping: 18 }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.94 }}
      className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-2 bg-[#041E42]/85 hover:bg-pshl-gold/95 hover:text-[#020A1A] text-white border border-pshl-gold/60 hover:border-pshl-gold font-display uppercase tracking-[0.18em] text-[11px] pl-3.5 pr-4 py-3 backdrop-blur-md shadow-[0_0_26px_rgba(255,215,0,0.22)] hover:shadow-[0_0_38px_rgba(255,215,0,0.55)] transition-colors duration-300"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span key="copied" initial={{ opacity: 0, rotate: -30 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0 }} className="inline-flex">
            <Check className="h-4 w-4" />
          </motion.span>
        ) : (
          <motion.span key="share" initial={{ opacity: 0, rotate: 30 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0 }} className="inline-flex">
            <Share2 className="h-4 w-4" />
          </motion.span>
        )}
      </AnimatePresence>
      <span className="hidden sm:inline">{copied ? "Link Copied" : "Share Page"}</span>
      <span className="sm:hidden">{copied ? "✓" : <Copy className="h-3 w-3" />}</span>
    </motion.button>
  );
}
